import { useQuery } from '@tanstack/react-query';

import { QUERY_KEYS, SECOND } from '../../../constants';
import { getData } from '../../../utils/api';
import { normalizeRoundValue } from './helpers';
import type {
  MatchResult,
  PreMatchWidgetData,
  PreMatchWidgetMatch,
  PreMatchWidgetStanding,
  PreMatchWidgetStandingLegendItem,
  StandingZone,
} from './types';

type EventStatisticsTeamIdFields = {
  id?: string | null;
  participantId?: string | null;
  teamId?: string | null;
  entityId?: string | null;
};

type EventStatisticsNamedTeam = EventStatisticsTeamIdFields & {
  name: string;
  shortName?: string | null;
};

type EventStatisticsScoredTeam = EventStatisticsNamedTeam & {
  score: number | null;
};

type EventStatisticsTrackedMatchResult = MatchResult | null;

type EventStatisticsLegacyNamedMatchTeam = {
  homeId?: string | null;
  homeParticipantId?: string | null;
  homeTeamId?: string | null;
  homeTeamName: string;
  homeTeamShortName?: string | null;
  awayId?: string | null;
  awayParticipantId?: string | null;
  awayTeamId?: string | null;
  awayTeamName: string;
  awayTeamShortName?: string | null;
};

type EventStatisticsLegacyScoredMatchTeam = EventStatisticsLegacyNamedMatchTeam & {
  homeScore: number | null;
  awayScore: number | null;
};

type EventStatisticsAggregateItem = {
  event: {
    eventId: string;
    statsId: string;
    competitionName: string | null;
    tournamentName: string | null;
    startDate: string;
    home?: EventStatisticsNamedTeam;
    away?: EventStatisticsNamedTeam;
    homeId?: string | null;
    homeParticipantId?: string | null;
    homeTeamId?: string | null;
    homeTeamName?: string;
    homeTeamShortName?: string | null;
    awayId?: string | null;
    awayParticipantId?: string | null;
    awayTeamId?: string | null;
    awayTeamName?: string;
    awayTeamShortName?: string | null;
  };
  venue: {
    name: string | null;
    city: string | null;
    region: string | null;
    capacity: number | null;
  } | null;
  summary: {
    form?: {
      home: ('W' | 'D' | 'L')[];
      away: ('W' | 'D' | 'L')[];
    };
    homeForm?: ('W' | 'D' | 'L')[];
    awayForm?: ('W' | 'D' | 'L')[];
    round?: string | number | null;
    leaguePositions: {
      home: number | null;
      away: number | null;
    };
  };
  standings: {
    zones?: {
      type: string;
      label: string | null;
    }[];
    tables: {
      overall: {
        position: number;
        teamName: string;
        teamShortName?: string | null;
        played: number;
        goalDifference: number;
        points: number;
        highlighted: boolean;
        zoneType: string | null;
      }[];
      home?: {
        position: number;
        teamName: string;
        teamShortName?: string | null;
        played: number;
        goalDifference: number;
        points: number;
        highlighted: boolean;
        zoneType: string | null;
      }[];
      away?: {
        position: number;
        teamName: string;
        teamShortName?: string | null;
        played: number;
        goalDifference: number;
        points: number;
        highlighted: boolean;
        zoneType: string | null;
      }[];
    };
  };
  lineups: {
    home: {
      starters: { name: string }[];
      substitutions: { name: string }[];
      coach: { name: string; role?: string | null } | null;
    };
    away: {
      starters: { name: string }[];
      substitutions: { name: string }[];
      coach: { name: string; role?: string | null } | null;
    };
    referees: { name: string; role?: string | null }[];
  };
  h2h: {
    totalMatches: number;
    homeWins: number;
    draws: number;
    awayWins: number;
    lastFiveMatches: (
      | {
          startDate: string;
          home: EventStatisticsScoredTeam;
          away: EventStatisticsScoredTeam;
        }
      | ({
          startDate: string;
        } & EventStatisticsLegacyScoredMatchTeam)
    )[];
  };
  lastFive: {
    home?: {
      fixtureId?: string | null;
      startDate: string;
      competitionName: string | null;
      home: EventStatisticsScoredTeam;
      away: EventStatisticsScoredTeam;
      resultForTrackedTeam?: EventStatisticsTrackedMatchResult;
    }[];
    away?: {
      fixtureId?: string | null;
      startDate: string;
      competitionName: string | null;
      home: EventStatisticsScoredTeam;
      away: EventStatisticsScoredTeam;
      resultForTrackedTeam?: EventStatisticsTrackedMatchResult;
    }[];
    homeTeamMatches?: ({
      startDate: string;
      competitionName: string | null;
    } & EventStatisticsLegacyScoredMatchTeam)[];
    awayTeamMatches?: ({
      startDate: string;
      competitionName: string | null;
    } & EventStatisticsLegacyScoredMatchTeam)[];
  };
  nextFive: {
    home?: {
      startDate: string;
      competitionName: string | null;
      home: EventStatisticsNamedTeam;
      away: EventStatisticsNamedTeam;
    }[];
    away?: {
      startDate: string;
      competitionName: string | null;
      home: EventStatisticsNamedTeam;
      away: EventStatisticsNamedTeam;
    }[];
    homeTeamFixtures?: ({
      startDate: string;
      competitionName: string | null;
    } & EventStatisticsLegacyNamedMatchTeam)[];
    awayTeamFixtures?: ({
      startDate: string;
      competitionName: string | null;
    } & EventStatisticsLegacyNamedMatchTeam)[];
  };
};

type EventStatisticsAggregateResponse =
  | EventStatisticsAggregateItem
  | {
      item: EventStatisticsAggregateItem;
    };

const getAggregateItem = (response: EventStatisticsAggregateResponse): EventStatisticsAggregateItem =>
  'item' in response ? response.item : response;

const mapZoneType = (zoneType: string | null): StandingZone => {
  const normalized = zoneType?.toLowerCase() ?? '';

  if (normalized.includes('champ')) return 'champions';
  if (normalized.includes('europa')) return 'europa';
  if (normalized.includes('conference')) return 'conference';
  if (normalized.includes('releg')) return 'relegation';
  if (normalized.includes('promotion')) return 'champions';

  return 'neutral';
};

const formatStandingZoneLabel = (zone: StandingZone): string => {
  if (zone === 'champions') return 'Champions League';
  if (zone === 'europa') return 'Europa League';
  if (zone === 'conference') return 'UEFA Conference League Qual.';
  if (zone === 'relegation') return 'Relegation';

  return 'Neutral';
};

const formatFallbackZoneLabel = (value: string | null): string | null => {
  if (!value) return null;

  return value
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const mapStanding = (row: {
  position: number;
  teamName: string;
  teamShortName?: string | null;
  played: number;
  goalDifference: number;
  points: number;
  highlighted: boolean;
  zoneType: string | null;
}): PreMatchWidgetStanding => ({
  position: row.position,
  teamName: row.teamName,
  teamShortName: row.teamShortName ?? null,
  played: row.played,
  goalDifference: row.goalDifference,
  points: row.points,
  highlighted: row.highlighted,
  zone: mapZoneType(row.zoneType),
});

const createStandingLegend = (
  zones: { type: string; label: string | null }[] | undefined,
  tables: PreMatchWidgetStanding[][]
): PreMatchWidgetStandingLegendItem[] => {
  const mappedZones =
    zones
      ?.map((zone) => {
        const mappedZone = mapZoneType(zone.type ?? null);

        if (mappedZone === 'neutral') return null;

        return {
          label: zone.label ?? formatFallbackZoneLabel(zone.type) ?? formatStandingZoneLabel(mappedZone),
          zone: mappedZone,
        };
      })
      .filter((zone): zone is PreMatchWidgetStandingLegendItem => !!zone) ?? [];

  if (mappedZones.length) {
    return mappedZones;
  }

  const derivedZones = new Set<Exclude<StandingZone, 'neutral'>>();

  tables.flat().forEach((row) => {
    if (row.zone && row.zone !== 'neutral') {
      derivedZones.add(row.zone);
    }
  });

  return [...derivedZones].map((zone) => ({
    label: formatStandingZoneLabel(zone),
    zone,
  }));
};

const mapScore = (homeScore: number | null, awayScore: number | null) => {
  if (homeScore == null || awayScore == null) return undefined;

  return `${homeScore} - ${awayScore}`;
};

const normalizeTrackedTeamResult = (value?: string | null): MatchResult | null => {
  if (value === 'W' || value === 'D' || value === 'L') {
    return value;
  }

  return null;
};

type EventStatisticsSideIdFields = {
  home?: EventStatisticsTeamIdFields;
  away?: EventStatisticsTeamIdFields;
  homeId?: string | null;
  homeParticipantId?: string | null;
  homeTeamId?: string | null;
  awayId?: string | null;
  awayParticipantId?: string | null;
  awayTeamId?: string | null;
};

const getTeamId = (team?: EventStatisticsTeamIdFields | null): string | null =>
  team?.id ?? team?.participantId ?? team?.teamId ?? team?.entityId ?? null;

const getSideTeamId = (value: EventStatisticsSideIdFields, side: 'home' | 'away'): string | null => {
  if (side === 'home') {
    return getTeamId(value.home) ?? value.homeParticipantId ?? value.homeTeamId ?? value.homeId ?? null;
  }

  return getTeamId(value.away) ?? value.awayParticipantId ?? value.awayTeamId ?? value.awayId ?? null;
};

const normalizeNamedTeams = (
  value: { home?: EventStatisticsNamedTeam; away?: EventStatisticsNamedTeam } & {
    homeId?: string | null;
    homeParticipantId?: string | null;
    homeTeamId?: string | null;
    homeTeamName?: string;
    homeTeamShortName?: string | null;
    awayId?: string | null;
    awayParticipantId?: string | null;
    awayTeamId?: string | null;
    awayTeamName?: string;
    awayTeamShortName?: string | null;
  }
) => ({
  home: {
    ...(value.home ?? {
      name: value.homeTeamName ?? '-',
      shortName: value.homeTeamShortName ?? null,
    }),
    id: getSideTeamId(value, 'home'),
  },
  away: {
    ...(value.away ?? {
      name: value.awayTeamName ?? '-',
      shortName: value.awayTeamShortName ?? null,
    }),
    id: getSideTeamId(value, 'away'),
  },
});

const getParticipantIdField = (participantId?: string | null) => (participantId ? { participantId } : {});
const getMatchParticipantIdField = (field: 'homeParticipantId' | 'awayParticipantId', participantId?: string | null) =>
  participantId ? { [field]: participantId } : {};

const normalizeScoredTeams = (
  value:
    | {
        home: EventStatisticsScoredTeam;
        away: EventStatisticsScoredTeam;
      }
    | EventStatisticsLegacyScoredMatchTeam
) => {
  if ('home' in value && 'away' in value) {
    return {
      home: {
        ...value.home,
        id: getSideTeamId(value, 'home'),
      },
      away: {
        ...value.away,
        id: getSideTeamId(value, 'away'),
      },
    };
  }

  return {
    home: {
      id: getSideTeamId(value, 'home'),
      name: value.homeTeamName,
      shortName: value.homeTeamShortName ?? null,
      score: value.homeScore,
    },
    away: {
      id: getSideTeamId(value, 'away'),
      name: value.awayTeamName,
      shortName: value.awayTeamShortName ?? null,
      score: value.awayScore,
    },
  };
};

const mapMatch = (
  match:
    | {
        fixtureId?: string | null;
        startDate: string;
        competitionName?: string | null;
        home: EventStatisticsNamedTeam & { score?: number | null };
        away: EventStatisticsNamedTeam & { score?: number | null };
        resultForTrackedTeam?: EventStatisticsTrackedMatchResult;
      }
    | ({
        startDate: string;
        competitionName?: string | null;
      } & EventStatisticsLegacyScoredMatchTeam)
    | ({
        startDate: string;
        competitionName?: string | null;
      } & EventStatisticsLegacyNamedMatchTeam),
  fallbackCompetition: string
): PreMatchWidgetMatch => {
  const teams = normalizeNamedTeams(match);
  const score =
    'homeScore' in match && 'awayScore' in match
      ? mapScore(match.homeScore, match.awayScore)
      : 'home' in match && 'away' in match
        ? mapScore(match.home.score ?? null, match.away.score ?? null)
        : undefined;

  return {
    fixtureId: 'fixtureId' in match ? (match.fixtureId ?? null) : null,
    date: match.startDate,
    competition: match.competitionName ?? fallbackCompetition,
    homeTeam: teams.home.name,
    homeTeamShortName: teams.home.shortName ?? null,
    ...getMatchParticipantIdField('homeParticipantId', teams.home.id),
    awayTeam: teams.away.name,
    awayTeamShortName: teams.away.shortName ?? null,
    ...getMatchParticipantIdField('awayParticipantId', teams.away.id),
    score,
    resultForTrackedTeam:
      'resultForTrackedTeam' in match ? normalizeTrackedTeamResult(match.resultForTrackedTeam) : null,
  };
};

export const mapPreMatchWidgetData = (response: EventStatisticsAggregateResponse): PreMatchWidgetData => {
  const { event, h2h, lastFive, lineups, nextFive, standings, summary, venue } = getAggregateItem(response);
  const competitionName = event.competitionName ?? '-';
  const tournamentName = event.tournamentName ?? competitionName;
  const fixture = normalizeNamedTeams(event);
  const leagueStandings = (standings.tables?.overall ?? []).map(mapStanding);
  const homeStandings = (standings.tables?.home ?? []).map(mapStanding);
  const awayStandings = (standings.tables?.away ?? []).map(mapStanding);

  return {
    competition: {
      name: competitionName,
    },
    tournament: {
      name: tournamentName,
      round: normalizeRoundValue(summary.round),
    },
    startDate: event.startDate,
    venue,
    fixture: {
      home: {
        name: fixture.home.name,
        shortName: fixture.home.shortName ?? null,
        ...getParticipantIdField(fixture.home.id),
      },
      away: {
        name: fixture.away.name,
        shortName: fixture.away.shortName ?? null,
        ...getParticipantIdField(fixture.away.id),
      },
    },
    preview: {
      homeForm: summary.form?.home ?? summary.homeForm ?? [],
      awayForm: summary.form?.away ?? summary.awayForm ?? [],
      leaguePositions: summary.leaguePositions,
    },
    standings: {
      league: leagueStandings,
      home: homeStandings,
      away: awayStandings,
      legend: createStandingLegend(standings.zones, [leagueStandings, homeStandings, awayStandings]),
    },
    lineups: {
      home: {
        starters: lineups.home.starters.map((player) => player.name),
        substitutes: lineups.home.substitutions.map((player) => player.name),
        coach: lineups.home.coach
          ? {
              name: lineups.home.coach.name,
              role: lineups.home.coach.role ?? null,
            }
          : null,
      },
      away: {
        starters: lineups.away.starters.map((player) => player.name),
        substitutes: lineups.away.substitutions.map((player) => player.name),
        coach: lineups.away.coach
          ? {
              name: lineups.away.coach.name,
              role: lineups.away.coach.role ?? null,
            }
          : null,
      },
      referees: lineups.referees.map((referee) => ({
        name: referee.name,
        role: referee.role ?? null,
      })),
    },
    headToHeadSummary: {
      totalMatches: h2h.totalMatches,
      homeWins: h2h.homeWins,
      draws: h2h.draws,
      awayWins: h2h.awayWins,
    },
    headToHead: h2h.lastFiveMatches.map((match) => {
      const teams = normalizeScoredTeams(match);

      return {
        date: match.startDate,
        competition: tournamentName,
        homeTeam: teams.home.name,
        homeTeamShortName: teams.home.shortName ?? null,
        ...getMatchParticipantIdField('homeParticipantId', teams.home.id),
        awayTeam: teams.away.name,
        awayTeamShortName: teams.away.shortName ?? null,
        ...getMatchParticipantIdField('awayParticipantId', teams.away.id),
        score: mapScore(teams.home.score, teams.away.score),
      };
    }),
    last5Matches: {
      home: (lastFive.home ?? lastFive.homeTeamMatches ?? []).map((match) => mapMatch(match, tournamentName)),
      away: (lastFive.away ?? lastFive.awayTeamMatches ?? []).map((match) => mapMatch(match, tournamentName)),
    },
    next5Matches: {
      home: (nextFive.home ?? nextFive.homeTeamFixtures ?? []).map((match) => mapMatch(match, tournamentName)),
      away: (nextFive.away ?? nextFive.awayTeamFixtures ?? []).map((match) => mapMatch(match, tournamentName)),
    },
  };
};

export const getPreMatchWidgetData = async (statsId: string): Promise<PreMatchWidgetData> => {
  const response = (await getData(`events/statistics/${statsId}`)) as EventStatisticsAggregateResponse;
  return mapPreMatchWidgetData(response);
};

const usePreMatchWidgetData = (statsId?: string | null) => {
  return useQuery({
    queryKey: [QUERY_KEYS.eventStatistics, statsId],
    queryFn: (): Promise<PreMatchWidgetData> => getPreMatchWidgetData(statsId as string),
    enabled: Boolean(statsId),
    staleTime: 60 * SECOND,
  });
};

export default usePreMatchWidgetData;
